import axios from "axios";

export const fetchCloudinaryCredentials = async (token, signal = null) => {
  // get request to the server to retrieve cloudinary credentials
  // token (String): bearer token used for authentication
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/media/cloudinary/credentials`,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const uploadCloudinaryImagesApi = async (token, images, signal) => {
  // post request to the cloudinary server to upload new images
  // token (String): bearer token used for authentication
  // images (Array of Files): the images to be uploaded
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  //  ...in cloudinary upload settings, defined settings:
  //  "quality": "auto" //compress w/o losing visual quality
  //  "height": "300" // Resize height to 300 pixels
  //  "resize": "fill"  //fill specified width/height
  //  "gravity": "auto" //smart cropping, keep objects in images
  //  "flags": "progressive" // Enable progressive loading (low then high qualty)
  //  "fetch_format": "auto" // Enable automatic format selection (eg webp for modern browsers)
  try {
    //get credentials from back end (cloud name)
    const { cloudName } = await fetchCloudinaryCredentials(token, signal);
    let uploadedImages = [];
    for (let i = 0; i < images.length; i++) {
      const formData = new FormData();
      formData.append("file", images[i]);
      formData.append("upload_preset", "e-commerce");
      //make cloudinary api call to upload images
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
        formData
      );
      uploadedImages.push({
        url: res.data.secure_url,
        public_id: res.data.public_id,
        format: res.data.format,
      });
    }
    // return data;
    return uploadedImages;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const uploadLargeCloudinaryImagesApi = async (token, images, signal) => {
  // post request to the cloudinary server to upload new images
  // token (String): bearer token used for authentication
  // images (Array of Files): the images to be uploaded
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  //  ...in cloudinary upload settings, defined settings:
  //  "quality": "auto" //compress w/o losing visual quality
  //  "width": "1520" // Resize height to 300 pixels
  //  "resize": "fill"  //fill specified width/height
  //  "gravity": "auto" //smart cropping, keep objects in images
  //  "flags": "progressive" // Enable progressive loading (low then high qualty)
  //  "fetch_format": "auto" // Enable automatic format selection (eg webp for modern browsers)
  try {
    //get credentials from back end (cloud name)
    const { cloudName } = await fetchCloudinaryCredentials(token, signal);
    let uploadedImages = [];
    for (let i = 0; i < images.length; i++) {
      const formData = new FormData();
      formData.append("file", images[i]);
      formData.append("upload_preset", "e-commerce-large");
      //make cloudinary api call to upload images
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
        formData
      );
      uploadedImages.push({
        url: res.data.secure_url,
        public_id: res.data.public_id,
        format: res.data.format,
      });
    }
    // return data;
    return uploadedImages;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const uploadLogoCloudinaryImagesApi = async (token, images, signal) => {
  // post request to the cloudinary server to upload new images
  // token (String): bearer token used for authentication
  // images (Array of Files): the images to be uploaded
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  //  ...in cloudinary upload settings, defined settings:
  //  "quality": "auto" //compress w/o losing visual quality
  //  "width": "50" // Resize height to 50 pixels
  //  "resize": "fill"  //fill specified width/height
  //  "gravity": "auto" //smart cropping, keep objects in images
  //  "flags": "progressive" // Enable progressive loading (low then high qualty)
  //  "fetch_format": "auto" // Enable automatic format selection (eg webp for modern browsers)
  try {
    //get credentials from back end (cloud name)
    const { cloudName } = await fetchCloudinaryCredentials(token, signal);
    let uploadedImages = [];
    for (let i = 0; i < images.length; i++) {
      const formData = new FormData();
      formData.append("file", images[i]);
      formData.append("upload_preset", "e-commerce-logo");
      //make cloudinary api call to upload images
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
        formData
      );
      uploadedImages.push({
        url: res.data.secure_url,
        public_id: res.data.public_id,
        format: res.data.format,
      });
    }
    // return data;
    return uploadedImages;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const uploadLargeCloudinaryVideosApi = async (token, videos, signal) => {
  // post request to the cloudinary server to upload new videos
  // token (String): bearer token used for authentication
  // videos (Array of Files): the videos to be uploaded
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  // in cloudinary upload settings, set:
  //  "quality": "auto" //compress w/o losing visual quality
  //  "width": "1520" // Resize
  //  "resize": "fill"  //fill specified width/height
  //  "gravity": "auto" //smart cropping, keep objects in images
  //  "flags": "progressive" // Enable progressive loading (low then high qualty)
  //  "fetch_format": "auto" // Enable automatic format selection (eg webp for modern browsers)
  try {
    //get credentials from back end (cloud name)
    const { cloudName } = await fetchCloudinaryCredentials(token, signal);
    let uploadedVideos = [];
    for (let i = 0; i < videos.length; i++) {
      const formData = new FormData();
      formData.append("file", videos[i]);
      formData.append("upload_preset", "e-commerce-video-large");
      //make cloudinary api call to upload images
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
        formData
      );
      uploadedVideos.push({
        url: res.data.secure_url,
        public_id: res.data.public_id,
        format: res.data.format,
      });
    }
    // return data;
    return uploadedVideos;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const deleteCloudinaryImagesApi = async (token, publicIdsArray) => {
  // delete request to the server to delete cloudinary images
  // token (String): bearer token used for authentication
  // publicIdsArray (Array of Strings): the public ids of the images to be deleted
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const publicIdsString = publicIdsArray.join(",");
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/media/images?publicIds=${publicIdsString}`,
      config
    );
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error?.response?.data?.message || "error");
      newErr.status = error?.response?.request?.status;
      throw newErr;
    }
  }
};

export const deleteCloudinaryVideosApi = async (token, publicIdsArray) => {
  // delete request to the server to delete cloudinary videos
  // token (String): bearer token used for authentication
  // publicIdsArray (Array of Strings): the public ids of the videos to be deleted
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const publicIdsString = publicIdsArray.join(",");
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/media/videos?publicIds=${publicIdsString}`,
      config
    );
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error?.response?.data?.message || "error");
      newErr.status = error?.response?.request?.status;
      throw newErr;
    }
  }
};
